import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import LayoutSingleViewFullWidth from '@/uiV2/common/layout/LayoutSingleViewFullWidth'
import Section from '@/components/Section'
import { MenuItems } from '@/enums/MenuItems'
import { Pages } from '@/Pages'
import Button from '@/components/Button'
import useKitConvoDebug from '@/uiV2/kitTest/hooks/useKitConvoDebug'
import KitDebugConsole from '@/uiV2/kitTest/components/KitDebugConsole'
import { kitTestThemeVars } from '@/uiV2/kitTest/theme'
import ProfilePic from '@/uiV2/common/person/ProfilePic'
import { formatPersonName } from '@/utils/personUtils'
import { formatDateAllInfo } from '@/utils/dateUtils'
import type { KitConvoStatusDto } from '@/api/kit/KitApi'

const formatDynamicVariablesJson = (value: string | null): string => {
    const trimmed = value?.trim() ?? ''
    if (!trimmed) {
        return '{}'
    }

    try {
        return JSON.stringify(JSON.parse(trimmed), null, 2)
    } catch {
        return trimmed
    }
}

const formatPayloadJson = (value: string | null): string => {
    const trimmed = value?.trim() ?? ''
    if (!trimmed) {
        return '{}'
    }

    try {
        return JSON.stringify(JSON.parse(trimmed), null, 2)
    } catch {
        return value ?? '{}'
    }
}

const renderConvoStatusBadge = (
    status: KitConvoStatusDto,
): React.JSX.Element => {
    if (status === 'Live') {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                </span>
                Live
            </span>
        )
    }

    if (status === 'Transcript Available') {
        return (
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                Transcript Available
            </span>
        )
    }

    if (status === 'No Transcription') {
        return (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                No Transcription
            </span>
        )
    }

    return (
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700">
            Call Ended
        </span>
    )
}

export default function KitConvoDebug(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const kitConvoId = searchParams.get('kitConvoId')?.trim() ?? ''
    const conversationId = searchParams.get('convId')?.trim() ?? ''
    const [isListening, setIsListening] = useState(false)
    const [hasCopiedRawPayload, setHasCopiedRawPayload] = useState(false)
    const copyResetTimerRef = useRef<number | null>(null)

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }, [])

    useEffect(() => {
        return () => {
            if (copyResetTimerRef.current) {
                window.clearTimeout(copyResetTimerRef.current)
            }
        }
    }, [])

    const {
        conversation,
        toolCalls,
        transcriptRawData,
        transcriptTurns,
        personMentions,
        companyMentions,
        locationMentions,
        targetJobTitleMentions,
        targetSalaryMentions,
        currentJobSearchParams,
        jobsOffered,
        jobSearchExecutions,
        scoring,
        toolCallsError,
        flashingToolCallIds,
        hasFetchedOnce,
        refreshDebugData,
    } = useKitConvoDebug({
        conversationId: conversationId || null,
        isActiveCall: isListening,
    })

    useEffect(() => {
        setIsListening(false)
        setHasCopiedRawPayload(false)
    }, [conversationId])

    const handleStopListening = useCallback(() => {
        setIsListening(false)
    }, [])

    const handleStartListening = useCallback(() => {
        if (!conversationId) return
        setIsListening(true)
    }, [conversationId])

    const copyTextToClipboard = useCallback(async (text: string) => {
        if (
            typeof navigator !== 'undefined' &&
            navigator.clipboard?.writeText
        ) {
            await navigator.clipboard.writeText(text)
            return
        }

        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (!copied) {
            throw new Error('Fallback copy failed')
        }
    }, [])

    const listeningButtonLabel = isListening
        ? 'Stop Listening for Updates'
        : 'Listen for Updates'

    const showLoadingState =
        Boolean(conversationId) && !hasFetchedOnce && !toolCallsError
    const isConversationLive = conversation?.status === 'Live'
    const canListenForUpdates = Boolean(conversationId) && isConversationLive

    const listenDisabledTooltip = !conversationId
        ? 'Provide a convId query parameter to enable listening for updates.'
        : !isConversationLive
          ? conversation
              ? `Listening for updates is only available when status is Live. Current status: ${conversation.status}.`
              : 'Listening for updates is only available when status is Live.'
          : null

    const formattedRawPayloadJson = formatPayloadJson(
        transcriptRawData?.payloadJson ?? null,
    )
    const canCopyRawPayload = Boolean(transcriptRawData)

    const handleCopyRawPayload = useCallback(async () => {
        if (!canCopyRawPayload) return

        try {
            await copyTextToClipboard(formattedRawPayloadJson)
            setHasCopiedRawPayload(true)
            if (copyResetTimerRef.current) {
                window.clearTimeout(copyResetTimerRef.current)
            }
            copyResetTimerRef.current = window.setTimeout(() => {
                setHasCopiedRawPayload(false)
            }, 2000)
        } catch (copyError) {
            console.error('Failed to copy ElevenLabs raw payload', copyError)
            setHasCopiedRawPayload(false)
        }
    }, [canCopyRawPayload, copyTextToClipboard, formattedRawPayloadJson])

    useEffect(() => {
        if (!canListenForUpdates && isListening) {
            setIsListening(false)
        }
    }, [canListenForUpdates, isListening])

    const debugConsole = !conversationId ? null : (
        <div className="mt-6" style={kitTestThemeVars}>
            <KitDebugConsole
                activeConvoId={conversationId}
                toolCalls={toolCalls}
                transcriptRawData={transcriptRawData}
                transcriptTurns={transcriptTurns}
                personMentions={personMentions}
                companyMentions={companyMentions}
                locationMentions={locationMentions}
                targetJobTitleMentions={targetJobTitleMentions}
                targetSalaryMentions={targetSalaryMentions}
                currentJobSearchParams={currentJobSearchParams}
                jobsOffered={jobsOffered}
                jobSearchExecutions={jobSearchExecutions}
                scoring={scoring}
                kitConvoId={kitConvoId || null}
                onRetriggerComplete={refreshDebugData}
                toolCallsError={toolCallsError}
                flashingToolCallIds={flashingToolCallIds}
            />
        </div>
    )

    const rawPayloadPanel = !conversationId ? null : (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                    ElevenLabs Raw Payload
                </h3>
                <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500">
                        Full stored JSON payload
                    </span>
                    <Button
                        type="button"
                        style="secondary"
                        size="small"
                        onClick={handleCopyRawPayload}
                        enabled={canCopyRawPayload}
                    >
                        {hasCopiedRawPayload ? 'Copied' : 'Copy JSON'}
                    </Button>
                </div>
            </div>
            {!transcriptRawData ? (
                <p className="mt-3 text-xs text-gray-500">
                    No transcript payload has been stored for this conversation
                    yet.
                </p>
            ) : (
                <pre className="mt-3 max-h-[640px] w-full overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed text-gray-800">
                    {formattedRawPayloadJson}
                </pre>
            )}
        </div>
    )

    const listenButtonControl = isListening ? (
        <Button type="button" style="mild-danger" onClick={handleStopListening}>
            {listeningButtonLabel}
        </Button>
    ) : (
        <Button
            type="button"
            style="primary"
            onClick={handleStartListening}
            enabled={canListenForUpdates}
        >
            {listeningButtonLabel}
        </Button>
    )

    const listenButton = listenDisabledTooltip ? (
        <div title={listenDisabledTooltip} className="inline-block">
            {listenButtonControl}
        </div>
    ) : (
        listenButtonControl
    )

    return (
        <LayoutSingleViewFullWidth activeMenuItem={MenuItems.Kit}>
            <Section title="Kit Conversation Debug Console">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="text-sm leading-tight text-gray-700">
                        <div>
                            <span className="font-semibold">KitConvo ID:</span>{' '}
                            {kitConvoId || 'Not provided'}
                        </div>
                        <div className="mt-1">
                            <span className="font-semibold">
                                Conversation ID:
                            </span>{' '}
                            {conversationId || 'Not provided'}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {listenButton}
                    </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                    <Link
                        to={`${Pages.Kit}?tab=conversations`}
                        className="text-blue-600 hover:underline"
                    >
                        ← Back to Kit conversations
                    </Link>
                </div>
                {!conversationId ? (
                    <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        Provide a `convId` query parameter to load the existing
                        tool calls and mentions for this Kit conversation.
                    </div>
                ) : (
                    <>
                        {conversation ? (
                            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    Conversation details
                                </h3>
                                <div className="mt-3 grid gap-3 md:grid-cols-2">
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                                            User
                                        </div>
                                        <div className="mt-2 text-xs text-gray-800">
                                            <div>
                                                <span className="font-semibold">
                                                    Email:
                                                </span>{' '}
                                                {conversation.user.email}
                                            </div>
                                            <div className="mt-1">
                                                <span className="font-semibold">
                                                    Name:
                                                </span>{' '}
                                                {conversation.user.name?.trim() ||
                                                    '—'}
                                            </div>
                                            <div className="mt-1">
                                                <span className="font-semibold">
                                                    User ID:
                                                </span>{' '}
                                                {conversation.user.id}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                                            Person
                                        </div>
                                        {conversation.person ? (
                                            <div className="mt-2 flex items-center gap-3">
                                                <ProfilePic
                                                    src={
                                                        conversation.person
                                                            .linkedInProfilePicUrl ??
                                                        undefined
                                                    }
                                                    size={36}
                                                />
                                                <div className="text-xs text-gray-800">
                                                    <div className="font-semibold text-gray-900">
                                                        {formatPersonName(
                                                            conversation.person,
                                                        ) || 'Unknown person'}
                                                    </div>
                                                    <div className="mt-1">
                                                        <span className="font-semibold">
                                                            Person ID:
                                                        </span>{' '}
                                                        {conversation.person.id}
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-2 text-xs text-gray-500">
                                                Not linked
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                        <span className="font-semibold">
                                            Created at:
                                        </span>{' '}
                                        {formatDateAllInfo(
                                            conversation.createdAt,
                                        )}
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                        <span className="font-semibold">
                                            Source:
                                        </span>{' '}
                                        {conversation.source?.trim() || '—'}
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                        <span className="font-semibold">
                                            Status:
                                        </span>{' '}
                                        {renderConvoStatusBadge(
                                            conversation.status,
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                                        Dynamic Variables
                                    </div>
                                    <pre className="mt-2 max-h-[320px] overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800">
                                        {formatDynamicVariablesJson(
                                            conversation.dynamicVariables,
                                        )}
                                    </pre>
                                </div>
                            </div>
                        ) : hasFetchedOnce && !toolCallsError ? (
                            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                                Conversation details were not found for this
                                convId.
                            </div>
                        ) : null}
                        {showLoadingState ? (
                            <div className="mt-6 text-sm text-gray-600">
                                Loading debug data…
                            </div>
                        ) : null}
                        {debugConsole}
                        {rawPayloadPanel}
                    </>
                )}
            </Section>
        </LayoutSingleViewFullWidth>
    )
}
